import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Layout from '../components/Layout'
import { supportedLanguages } from '../i18n'

function Settings() {
  const { t, i18n } = useTranslation()
  const [isDark, setIsDark] = useState(true)
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language?.split('-')[0] || 'en')

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme')
    setIsDark(savedTheme !== 'light')
  }, [])

  const toggleDarkMode = () => {
    const newValue = !isDark
    setIsDark(newValue)
    localStorage.setItem('theme', newValue ? 'dark' : 'light')
    
    if (newValue) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem('language', lang)
    setCurrentLanguage(lang)
  }

  const clearAllData = () => {
    if (confirm(t('settings.clearConfirm') || 'Are you sure you want to clear all data? This cannot be undone.')) {
      localStorage.clear()
      window.location.reload()
    }
  }

  return (
    <Layout>
      <div className="flex flex-col p-4 gap-6">
        <h2 className="text-slate-900 dark:text-white text-xl font-bold">{t('settings.title')}</h2>

        {/* Appearance Section */}
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {t('settings.appearance')}
          </h3>
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-indigo-500">dark_mode</span>
                </div>
                <div>
                  <h4 className="text-slate-900 dark:text-white font-medium text-sm">{t('settings.darkMode')}</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">{t('settings.darkModeDescription')}</p>
                </div>
              </div>
              <button
                onClick={toggleDarkMode}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  isDark ? 'bg-primary' : 'bg-slate-300'
                }`}
              >
                <span 
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200 ${
                    isDark ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Language Section */}
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {t('settings.language')}
          </h3>
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-green-500">language</span>
                </div>
                <div>
                  <h4 className="text-slate-900 dark:text-white font-medium text-sm">{t('settings.language')}</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">{t('settings.languageDescription')}</p>
                </div>
              </div>
              <select
                value={currentLanguage}
                onChange={(e) => changeLanguage(e.target.value)}
                className="bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg px-3 py-2 text-sm border-none focus:ring-2 focus:ring-primary"
              >
                {supportedLanguages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.nativeName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            Account
          </h3>
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <Link to="/auth" className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-violet-500">person</span>
                </div>
                <div>
                  <h4 className="text-slate-900 dark:text-white font-medium text-sm">Login / Sign Up</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">Supabase Auth</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-slate-400 text-sm">arrow_forward_ios</span>
            </Link>
          </div>
        </div>

        {/* About Section */}
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {t('settings.about')}
          </h3>
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
            <Link to="/about" className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-500">info</span>
                </div>
                <div>
                  <h4 className="text-slate-900 dark:text-white font-medium text-sm">{t('settings.about')}</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">{t('settings.aboutDescription')}</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-slate-400 text-sm">arrow_forward_ios</span>
            </Link>
            <Link to="/pricing" className="flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-amber-500 filled">crown</span>
                </div>
                <div>
                  <h4 className="text-slate-900 dark:text-white font-medium text-sm">{t('settings.premium')}</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">{t('settings.premiumDescription')}</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-slate-400 text-sm">arrow_forward_ios</span>
            </Link>
          </div>
        </div>

        {/* Data Section */}
        <div className="flex flex-col gap-3">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            {t('settings.data')}
          </h3>
          <div className="bg-white dark:bg-surface-dark rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <button 
              onClick={clearAllData}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-red-500">delete_forever</span>
                </div>
                <div className="text-left">
                  <h4 className="text-red-600 dark:text-red-400 font-medium text-sm">{t('settings.clearAllData')}</h4>
                  <p className="text-slate-500 dark:text-slate-400 text-xs">{t('settings.clearAllDataDescription')}</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Version Info */}
        <div className="text-center pt-4">
          <p className="text-slate-400 text-xs">{t('settings.version')}</p>
          <p className="text-slate-400 text-xs mt-1">{t('settings.processingNote')}</p>
        </div>
      </div>
    </Layout>
  )
}

export default Settings
